import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import { siteConfig } from '@/constants/site'
import { Logo } from '@/components/generals/logo'
import { footerColumns, socialLinks } from '@/constants/navigation'

const join = (...values: (string | false | undefined)[]): string =>
    values.filter(Boolean).join(' ')

export const Footer: FC = (): ReactNode => {
    const router = useRouter()
    const year = 2026

    return (
        <section className='footer-section section-padding'>
            <div className='w-layout-blockcontainer container w-container'>
                <div className='footer-content-wrapper'>
                    <div className='footer-top'>
                        <div className='footer-top-left'>
                            <div className='footer-cta'>
                                <p className='footer-cta-title'>
                                    Need Help? Call Now:
                                </p>

                                <a href={siteConfig.phoneHref} className='footer-cta-link'>
                                    {siteConfig.phone}
                                </a>
                            </div>

                            <div className='footer-location-wrapper'>
                                <p className='footer-location-title'>
                                    Based in:
                                </p>

                                <a
                                    href={siteConfig.mapUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='footer-location'
                                >
                                    {siteConfig.address}
                                </a>
                            </div>
                        </div>

                        <div className='footer-link-area'>
                            {footerColumns.map((column) => (
                                <div key={column.title} className='footer-link-wrapper'>
                                    <p className='footer-link-title'>
                                        {column.title}
                                    </p>

                                    <div className='single-footer-link-list'>
                                        {column.links.map((link) => (
                                            <Link
                                                key={link.href + link.label}
                                                href={link.href}
                                                className={join(
                                                    'footer-link',
                                                    router.pathname === link.href && 'w--current'
                                                )}
                                            >
                                                {link.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='footer-bottom'>
                        <Link
                            href='/'
                            className={join('w-inline-block', router.pathname === '/' && 'w--current')}
                            aria-label={`${siteConfig.name} home`}
                        >
                            <Logo className='footer-logo' />
                        </Link>

                        <div className='footer-bottom-right'>
                            {socialLinks.length > 0 && (
                                <div className='footer-socials'>
                                    {socialLinks.map((social) => (
                                        <a
                                            key={social.label}
                                            href={social.href}
                                            target='_blank'
                                            rel='noreferrer'
                                            aria-label={social.label}
                                            className='footer-social-link w-inline-block'
                                        >
                                            <img
                                                src={social.icon}
                                                loading='lazy'
                                                alt=''
                                                className='footer-social-icon'
                                            />

                                            <img
                                                src={social.hoverIcon}
                                                loading='lazy'
                                                alt=''
                                                className='footer-social-icon hover'
                                            />
                                        </a>
                                    ))}
                                </div>
                            )}

                            <a href={`mailto:${siteConfig.email}`} className='footer-link'>
                                {siteConfig.email}
                            </a>

                            <p className='copyright-text'>
                                {`Copyright © ${year} ${siteConfig.name}. All rights reserved.`}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
